export const isActiveCleaningJob = (job) => ["queued", "processing"].includes(job?.status);
export const isTerminalCleaningJob = (job) => ["completed", "failed", "cancelled", "released"].includes(job?.status);

/** One boundary for the private GET, shared lock, apply envelope and socket identities. */
export function normalizeCleaningJob(input, { datasetId = null, event = null, shared = false } = {}) {
  const raw = input?.job !== undefined ? input.job : input;
  if (!raw || typeof raw !== "object") return null;
  const jobId = raw.jobId ?? raw.job_id ?? raw.id;
  const target = raw.datasetId ?? raw.dataset_id ?? datasetId;
  const eventStatus = {
    "cleaning:job:queued": "queued", "cleaning:job:progress": "processing",
    "cleaning:job:completed": "completed", "cleaning:job:failed": "failed",
  }[event];
  const status = eventStatus ?? raw.status;
  if (jobId == null || target == null || !["queued", "processing", "completed", "failed", "cancelled", "released"].includes(status)) return null;
  const count = (v) => typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
  return {
    jobId: String(jobId), datasetId: String(target), status,
    sourceKind: raw.sourceKind || raw.source_kind || raw.source?.kind || "import_job",
    progress: status === "completed" ? 100 : count(raw.progress) != null ? Math.min(100, raw.progress) : status === "queued" ? 0 : null,
    stage: raw.stage ?? (status === "queued" ? "queued" : null),
    errorCode: raw.errorCode ?? raw.error_code ?? null,
    affectedCells: count(raw.affectedCells ?? raw.affected_cells),
    // GET has no result. Never promote preview affected_cells to a completed net count.
    result: event === "cleaning:job:completed" ? {
      affected_cells: count(raw.result?.affected_cells), columns_count: count(raw.result?.columns_count),
      change_id: raw.result?.change_id ?? null, no_op: raw.result?.no_op === true,
    } : raw.result ?? null,
    shared,
  };
}

/** A terminal state and real progress cannot be rolled back by a late GET/event. */
export function mergeCleaningJob(previous, next) {
  if (!next) return previous;
  if (!previous || previous.jobId !== next.jobId || previous.datasetId !== next.datasetId || previous.sourceKind !== next.sourceKind) return next;
  if (isTerminalCleaningJob(previous)) {
    // An event can enrich an earlier completed GET without reopening the job or repeating effects.
    if (previous.status === "completed" && next.status === "completed" && !previous.result && next.result) return { ...previous, result: next.result };
    return previous;
  }
  const stages = ["queued", "computing", "writing", "done"];
  return {
    ...previous, ...next,
    status: previous.status === "processing" && next.status === "queued" ? "processing" : next.status,
    progress: previous.progress == null && next.progress == null ? null : Math.max(previous.progress ?? 0, next.progress ?? 0),
    stage: stages.indexOf(previous.stage) > stages.indexOf(next.stage) ? previous.stage : next.stage,
    result: next.result ?? previous.result,
  };
}

/** Keep sync callers' shape; only job envelopes get an explicit discriminator. */
export function normalizeCleaningApply(body, httpStatus) {
  if (body?.job_id && ["queued", "processing", "failed", "cancelled"].includes(body.status)) {
    return { ...body, async: true, jobId: String(body.job_id) };
  }
  if (httpStatus === 202) {
    const err = new Error("Invalid cleaning job response");
    err.code = "CLEANING_JOB_RESPONSE_INVALID";
    throw err;
  }
  return body;
}
